import { isDefined } from '@kaoto/forms';
import { cloneDeep, unset } from 'lodash';

import { getCamelRandomId } from '../../../camel-utils/camel-random-id';
import { DynamicCatalogRegistry } from '../../../dynamic-catalog/dynamic-catalog-registry';
import { getArrayProperty, getValue, setValue } from '../../../utils';
import { DefinedComponent } from '../../camel/camel-catalog-index';
import { CatalogKind } from '../../catalog-kind';
import { CITRUS_TEST_ROOT_ENTITY_NAME } from '../../citrus/citrus-catalog-index';
import { Test, TestAction, TestActions } from '../../citrus/entities/Test';
import { EntityType } from '../../entities';
import { KaotoSchemaDefinition } from '../../kaoto-schema';
import { PlaceholderType } from '../../placeholder.constants';
import { NodeLabelType } from '../../settings';
import {
  AddStepMode,
  BaseVisualEntity,
  IVisualizationNode,
  IVisualizationNodeData,
  IVisualizationNodeIds,
  NodeInteraction,
} from '../base-visual-entity';
import { IClipboardContent } from '../clipboard';
import { NodeIdentity } from '../node-identity';
import { createVisualizationNode } from '../visualization-node';
import { NodeEnrichmentService } from './nodes/node-enrichment.service';
import { CitrusTestDefaultService } from './support/citrus-test-default.service';
import { CitrusTestContainerSettings, CitrusTestSchemaService } from './support/citrus-test-schema.service';
import { ModelValidationService } from './support/validators/model-validation.service';

/**
 * Type guard to determine whether an object is a Citrus Test.
 *
 * Performs a basic structural check for required properties:
 * - Must be a non-array object
 * - Must have more than one property
 * - Must have 'name' and 'actions' properties
 *
 * @param rawEntity - The object to check
 * @returns True if the object appears to be a Citrus Test
 */
export const isCitrusTest = (rawEntity: unknown): rawEntity is Test => {
  if (!isDefined(rawEntity) || Array.isArray(rawEntity) || typeof rawEntity !== 'object') {
    return false;
  }

  const objectKeys = Object.keys(rawEntity);

  return objectKeys.length > 1 && 'name' in rawEntity && 'actions' in rawEntity;
};

/**
 * Creates a default Citrus test with basic structure.
 *
 * The default test includes:
 * - A name
 * - An empty variables array
 * - Two sample actions: createVariables and print
 *
 * @param name - The name for the test
 * @returns A Test object with default structure
 */
const getDefaultTest = (name: string): Test => {
  return {
    name: `${name}`,
    variables: [],
    actions: [
      {
        createVariables: { variables: [{ name: 'user', value: 'Citrus' }] },
      },
      {
        print: { message: 'Hello from ${user}' },
      },
    ],
  };
};

/**
 * Visual entity representation of a Citrus test in the Kaoto editor.
 *
 * This class implements BaseVisualCamelEntity to provide visual representation
 * and manipulation capabilities for Citrus tests. It handles:
 * - Converting test definitions to visualization nodes
 * - Managing test actions and their hierarchy
 * - Providing schemas for property forms
 * - Handling add/remove/update operations on test actions
 * - Supporting drag-and-drop and copy-paste operations
 *
 * The visual entity manages the complete lifecycle of a Citrus test in the editor,
 * including nested containers and action groups.
 */
export class CitrusTestVisualEntity implements BaseVisualEntity {
  id: string;
  readonly type = EntityType.Test;
  static readonly ROOT_PATH = 'test';

  constructor(public test: Test) {
    this.id = test?.name ?? getCamelRandomId('test');
    if (!isDefined(this.test)) {
      this.test = getDefaultTest(this.id);
      this.test.name = this.id;
    }
  }

  /**
   * Gets the root path for this entity in the visualization tree.
   */
  getRootPath(): string {
    return CitrusTestVisualEntity.ROOT_PATH;
  }

  /**
   * Type guard to check if a definition is applicable for this entity type.
   *
   * @param testDef - The definition to check
   * @returns True if the definition is a valid Citrus test
   */
  static isApplicable(testDef: unknown): testDef is Test {
    return isCitrusTest(testDef);
  }

  /** Internal API methods */
  getId(): string {
    return this.id;
  }

  setId(testName: string): void {
    this.id = testName;
    this.test.name = this.id;
  }

  /**
   * Gets the display label for a node at the given path.
   *
   * For the root path, returns the test ID.
   * For action paths, extracts and formats the action name.
   *
   * @param path - The path to the node in the visualization tree
   * @returns The display label for the node
   */
  getNodeLabel(path?: string, labelType?: NodeLabelType): string {
    if (!path) return '';

    if (path === this.getRootPath()) {
      const description: string | undefined = this.test.description;
      if (labelType === NodeLabelType.Description && description) {
        return description;
      }

      return this.id;
    }

    // Check if we should return description for child actions
    if (labelType === NodeLabelType.Description) {
      const actionModel: TestAction = getValue(this.test, this.toModelPath(path));
      const description = actionModel?.description;
      if (description) {
        return description;
      }
    }

    const name = path.split('.').pop() || '';
    const tokens = name.split('-');
    if (tokens.length > 2) {
      return tokens.slice(1).join('-');
    } else if (tokens.length > 1 && name.length > 18) {
      // name too long - use shortcut
      return tokens.pop() || '';
    }

    return name;
  }

  async fetchNodeSchema(ids: IVisualizationNodeIds): Promise<KaotoSchemaDefinition['schema'] | undefined> {
    if (!ids?.primaryNodeId) {
      return;
    }

    if (ids.primaryNodeId.catalogKind === CatalogKind.Entity) {
      // Root test group node — use the dynamic catalog registry
      return await this.getRootTestSchema();
    }

    // Test action / container nodes
    const result = await CitrusTestSchemaService.getTestActionDefinition(ids.primaryNodeId.name);
    return result?.definition.propertiesSchema || ({} as KaotoSchemaDefinition['schema']);
  }

  async fetchNodeDefinition(path?: string, ids?: IVisualizationNodeIds): Promise<unknown> {
    if (!path || !ids?.primaryNodeId?.name) return undefined;
    if (path === this.getRootPath()) {
      return this.test;
    }
    const actionName = ids.primaryNodeId.name;
    const actionModel: TestAction = getValue(this.test, this.toModelPath(path));
    if (actionModel) {
      await this.updateTestActionModel(path, actionName, actionModel);
    }
    return actionModel ?? {};
  }

  getOmitFormFields(): string[] {
    return [];
  }

  toJSON(): Test {
    return this.test;
  }

  /**
   * Runs the group-model normalisation pass in-place on `this.test.actions`.
   *
   * Must be called before serialising to YAML (i.e. from `toSourceCode()`).
   * Moves properties that belong to ancestor test action groups from the flat
   * action model back into the correct nested YAML hierarchy.
   *
   * Separated from `toJSON()` so that normalisation can be made async and
   * eventually read from the DynamicCatalogRegistry.
   */
  async normaliseForSerialisation(): Promise<Test> {
    const snapshot = cloneDeep(this.test);
    await this.updateTestGroupModel(snapshot.actions);
    await this.updateTestGroupModel(snapshot.finally ?? []);
    return snapshot;
  }

  updateModel(path: string | undefined, value: Record<string, unknown>): void {
    if (!path) return;

    if (path === this.getRootPath()) {
      this.id = this.test.name;
      return;
    }

    setValue(this.test, this.toModelPath(path), value);
  }

  /**
   * Adds a test action step to the test.
   *
   * Creates a default action definition from the catalog component and adds it
   * to the test at the appropriate location based on the mode and target data.
   *
   * Path examples:
   * - 'variables'
   * - 'actions.0'
   * - 'actions.1.iterate.0'
   *
   * @param options - Configuration options for adding the step including
   * - The catalog component definition to add
   * - How to add the step (append, prepend, or replace)
   * - Visualization node data indicating where to add the step
   */
  addStep(options: {
    definedComponent: DefinedComponent;
    mode: AddStepMode;
    data: IVisualizationNodeData;
    targetProperty?: string;
  }) {
    const action = CitrusTestDefaultService.getDefaultTestActionDefinitionValue(options.definedComponent);
    return this.addNewStep(action, options.mode, options.data);
  }

  getCopiedContent(path?: string, ids?: IVisualizationNodeIds): IClipboardContent | undefined {
    if (!path || !ids?.primaryNodeId?.name) return;

    const actionName = ids.primaryNodeId.name;

    // Allow copying the entire test entity
    if (path === this.getRootPath()) {
      return { name: actionName, definition: this.test as object };
    }

    const actionModel: TestActions = getValue(this.test, this.toModelPath(path.substring(0, path.lastIndexOf('.'))));
    return { name: actionName, definition: actionModel as object };
  }

  /**
   * Pastes a copied test action step into the test.
   *
   * @param options - Configuration options for pasting the step including
   * - The copied content to paste
   * - How to add the step (append, prepend, or replace)
   * - Visualization node data indicating where to paste
   */
  pasteStep(options: { clipboardContent: IClipboardContent; mode: AddStepMode; data: IVisualizationNodeData }) {
    const action = options.clipboardContent.definition as TestActions;
    this.addNewStep(action, options.mode, options.data);
  }

  canDragNode(path?: string) {
    return isDefined(path);
  }

  canDropOnNode(path?: string) {
    return this.canDragNode(path);
  }

  removeStep(path?: string): void {
    if (!path) return;

    const pathArray = path.split('.');
    const last = pathArray[pathArray.length - 1];
    const penultimate = pathArray[pathArray.length - 2];

    /**
     * If the last segment is a number, it means the target object is a member of an array
     * therefore we need to look for the array and remove the element at the given index
     *
     * f.i. actions.1.soap-assertFault.when.myAction
     * last: myAction
     * penultimate: when
     */
    if (!Number.isInteger(Number(last)) && !Number.isInteger(Number(penultimate))) {
      const action = getValue(this.test, this.toModelPath(pathArray.slice(0, -2).join('.')), []);
      setValue(action, penultimate, undefined);
      return;
    }

    /**
     * If the last segment is a number, it means the target object is a member of an array
     * therefore we need to look for the array and remove the element at the given index
     *
     * f.i. actions.1.iterate.0
     * last: 0
     */
    let array = getValue(this.test, this.toModelPath(pathArray.slice(0, -1).join('.')), []);
    if (Number.isInteger(Number(last)) && Array.isArray(array)) {
      array.splice(Number(last), 1);

      return;
    }

    /**
     * If the last segment is a word and the penultimate is a number, it means the target is an object
     * potentially a Processor, that belongs to an array, therefore we remove it entirely
     *
     * f.i. actions.1.iterate
     * last: iterate
     * penultimate: 1
     */
    array = getValue(this.test, this.toModelPath(pathArray.slice(0, -2).join('.')), []);
    if (!Number.isInteger(Number(last)) && Number.isInteger(Number(penultimate)) && Array.isArray(array)) {
      array.splice(Number(penultimate), 1);
    }
  }

  getNodeInteraction(data: IVisualizationNodeData): NodeInteraction {
    return {
      /** Test cannot have actions before the variables declaration */
      canHavePreviousStep: data.path !== this.getRootPath() && data.path !== 'variables',
      /** Test cannot have actions after the finally block */
      canHaveNextStep: data.path !== 'finally',
      canHaveChildren: data.isGroup ?? false,
      canHaveSpecialChildren: false,
      canReplaceStep: data.path !== this.getRootPath(),
      canRemoveStep: data.path !== this.getRootPath(),
      canRemoveFlow: data.path === this.getRootPath(),
      canBeDisabled: false,
    };
  }

  async getNodeValidationText(
    path?: string,
    schema?: KaotoSchemaDefinition['schema'],
    ids?: IVisualizationNodeIds,
  ): Promise<string | undefined> {
    const definition = await this.fetchNodeDefinition(path, ids);
    if (!schema || !definition) return undefined;

    return await ModelValidationService.validateNodeStatus(schema, definition);
  }

  getGroupIcons(): { icon: string; title: string }[] {
    return [];
  }

  async toVizNode(): Promise<IVisualizationNode> {
    const testGroupNode = createVisualizationNode(this.id, {
      name: this.type,
      path: this.getRootPath(),
      entity: this,
      isPlaceholder: false,
      isGroup: true,
      iconUrl: '',
      title: '',
      description: '',
      processorIconTooltip: '',
      primaryNodeId: { name: this.type, catalogKind: CatalogKind.Entity } satisfies NodeIdentity,
    });

    const actionNodes = await this.getVizNodesFromSteps(this.test.actions, this.getRootPath());
    actionNodes.forEach((actionNode) => {
      testGroupNode.addChild(actionNode);
    });

    await NodeEnrichmentService.enrichVisualizationTree(testGroupNode);

    return testGroupNode;
  }

  private addNewStep(action: TestActions, mode: AddStepMode, data: IVisualizationNodeData) {
    const path = data.path;
    if (!path) return;

    const pathArray = path.split('.');
    const last = pathArray[pathArray.length - 1];
    const penultimate = pathArray[pathArray.length - 2];

    /**
     * If the last segment is a string and the penultimate is a number, it means the target is member of an array
     * therefore we need to look for the array and insert the element at the given index + 1
     *
     * f.i. actions.0.echo
     * penultimate: 0
     * last: echo
     */
    if (!Number.isInteger(Number(last)) && Number.isInteger(Number(penultimate))) {
      /** If we're in Append mode, we need to insert the step after the selected index hence `Number(penultimate) + 1` */
      const desiredStartIndex = mode === AddStepMode.AppendStep ? Number(penultimate) + 1 : Number(penultimate);

      /** If we're in Replace mode, we need to delete the existing step */
      const deleteCount = mode === AddStepMode.ReplaceStep ? 1 : 0;

      const array: TestActions[] = getArrayProperty(this.test, this.toModelPath(pathArray.slice(0, -2).join('.')));
      array.splice(desiredStartIndex, deleteCount, action);

      return [...pathArray.slice(0, -2), desiredStartIndex].join('.');
    }

    /**
     * If the last segment is a string and the penultimate is a string, too, it means the target is a single nested node
     * therefore we need to look for the parent container and insert.
     *
     * f.i. actions.catch.when.placeholder
     * penultimate: when
     * last: placeholder
     */
    if (!Number.isInteger(Number(last)) && !Number.isInteger(Number(penultimate))) {
      /** Whatever mode append or replace, we replace the current node as we have a single nested node */
      setValue(this.test, this.toModelPath(path.substring(0, path.lastIndexOf('.'))), action);
      return path.substring(0, path.lastIndexOf('.'));
    }

    /**
     * If the last segment is a number and the penultimate is a string, it also means the target is member of an array
     *
     * f.i. actions.0.iterate.0
     * penultimate: iterate
     * last: 0
     */
    if (Number.isInteger(Number(last)) && !Number.isInteger(Number(penultimate))) {
      /** If we're in Append mode, we need to insert the step after the selected index hence `Number(last) + 1` */
      const desiredStartIndex = mode === AddStepMode.AppendStep ? Number(last) + 1 : Number(last);

      /** If we're in Replace mode, we need to delete the existing step */
      const deleteCount = mode === AddStepMode.ReplaceStep ? 1 : 0;

      const array: TestActions[] = getArrayProperty(this.test, this.toModelPath(pathArray.slice(0, -1).join('.')));
      array.splice(desiredStartIndex, deleteCount, action);
      return [...pathArray.slice(0, -1), desiredStartIndex].join('.');
    }
  }

  private async getVizNodeFromStep(
    action: TestActions,
    path: string,
    preResolvedName?: string,
  ): Promise<IVisualizationNode> {
    const actionName = preResolvedName ?? (await CitrusTestSchemaService.getTestActionName(action));
    const data: IVisualizationNodeData = {
      name: actionName,
      path,
      entity: this,
      isPlaceholder: false,
      isGroup: false,
      iconUrl: '',
      title: '',
      description: '',
      processorIconTooltip: '',
      primaryNodeId: { name: actionName, catalogKind: CatalogKind.TestAction } satisfies NodeIdentity,
    };

    const vizNode = createVisualizationNode(path, data);

    const containerSettings = CitrusTestSchemaService.getTestContainerSettings(actionName);
    if (containerSettings) {
      vizNode.data.isGroup = true;

      const childrenVizNodes = await this.getVizNodesFromChildren(this.toModelPath(path), containerSettings);

      childrenVizNodes.forEach((childVizNode) => {
        vizNode.addChild(childVizNode);
      });
    }

    return vizNode;
  }

  private async getVizNodesFromSteps(actions: TestActions[], path: string): Promise<IVisualizationNode[]> {
    if (!Array.isArray(actions)) {
      return [];
    }
    const actionsPath = path === this.getRootPath() ? 'actions' : path;

    const vizNodes: IVisualizationNode[] = [];
    for (let index = 0; index < actions.length; index++) {
      const action = actions[index];
      const actionName = await CitrusTestSchemaService.getTestActionName(action);
      const vizNode = await this.getVizNodeFromStep(action, `${actionsPath}.${index}.${actionName}`, actionName);

      if (index > 0) {
        const previousVizNode = vizNodes[index - 1];
        previousVizNode.setNextNode(vizNode);
        vizNode.setPreviousNode(previousVizNode);
      }

      vizNodes.push(vizNode);
    }

    /** Empty steps branch placeholder */
    const placeholderPath = `${actionsPath}.${vizNodes.length}.placeholder`;
    const previousNode = vizNodes[vizNodes.length - 1];
    const placeholderNode = createVisualizationNode(placeholderPath, {
      name: PlaceholderType.Placeholder,
      isPlaceholder: true,
      isGroup: false,
      iconUrl: '',
      title: '',
      description: '',
      processorIconTooltip: '',
      path: placeholderPath,
      primaryNodeId: { name: PlaceholderType.Placeholder, catalogKind: CatalogKind.TestAction } satisfies NodeIdentity,
    });
    vizNodes.push(placeholderNode);

    if (previousNode) {
      previousNode.setNextNode(placeholderNode);
      placeholderNode.setPreviousNode(previousNode);
    }
    return vizNodes;
  }

  private async getRootTestSchema(): Promise<KaotoSchemaDefinition['schema']> {
    const testRootDefinition = await DynamicCatalogRegistry.get().getEntity(
      CatalogKind.Entity,
      CITRUS_TEST_ROOT_ENTITY_NAME,
    );
    const schema = cloneDeep(testRootDefinition?.propertiesSchema ?? {});

    if (schema.properties) {
      // remove actions and finally from test schema because the properties panel should not display those items.
      schema.properties['actions'] = {};
      schema.properties['finally'] = {};
    }

    return schema;
  }

  protected async getVizNodesFromChildren(
    path: string,
    containerSettings: CitrusTestContainerSettings,
  ): Promise<IVisualizationNode[]> {
    const subpath = `${path}.${containerSettings.name}`;
    switch (containerSettings.type) {
      case 'single-node':
        return this.getChildrenFromSingleClause(subpath);

      case 'branch':
        return this.getChildrenFromBranch(subpath);

      case 'array-node':
        return this.getChildrenFromArrayClause(subpath);

      default:
        return [];
    }
  }

  protected async getChildrenFromSingleClause(path: string): Promise<IVisualizationNode[]> {
    const action: TestActions = getValue(this.test, path);
    if (action === undefined) {
      /** Empty steps branch placeholder */
      const placeholderPath = `${path}.placeholder`;
      const placeholderNode = createVisualizationNode(placeholderPath, {
        name: PlaceholderType.Placeholder,
        isPlaceholder: true,
        isGroup: false,
        iconUrl: '',
        title: '',
        description: '',
        processorIconTooltip: '',
        path: placeholderPath,
        primaryNodeId: {
          name: PlaceholderType.Placeholder,
          catalogKind: CatalogKind.TestAction,
        } satisfies NodeIdentity,
      });
      return [placeholderNode];
    }

    const actionName = await CitrusTestSchemaService.getTestActionName(action);
    const stepVizNode = await this.getVizNodeFromStep(action, `${path}.${actionName}`, actionName);
    return [stepVizNode];
  }

  protected async getChildrenFromBranch(path: string): Promise<IVisualizationNode[]> {
    const actions: TestActions[] = getValue(this.test, path, []);
    return this.getVizNodesFromSteps(actions, path);
  }

  protected async getChildrenFromArrayClause(path: string): Promise<IVisualizationNode[]> {
    const actions: TestActions[] = getValue(this.test, path, []);

    const children: IVisualizationNode[] = [];
    for (let index = 0; index < actions.length; index++) {
      const action = actions[index];
      const actionName = await CitrusTestSchemaService.getTestActionName(action);
      const vizNode = await this.getVizNodeFromStep(action, `${path}.${index}.${actionName}`, actionName);
      children.push(vizNode);
    }

    /** Empty steps branch placeholder */
    const placeholderPath = `${path}.${children.length}.placeholder`;
    const previousNode = children[children.length - 1];
    const placeholderNode = createVisualizationNode(placeholderPath, {
      name: PlaceholderType.Placeholder,
      isPlaceholder: true,
      isGroup: false,
      iconUrl: '',
      title: '',
      description: '',
      processorIconTooltip: '',
      path: placeholderPath,
      primaryNodeId: { name: PlaceholderType.Placeholder, catalogKind: CatalogKind.TestAction } satisfies NodeIdentity,
    });
    children.push(placeholderNode);

    if (previousNode) {
      previousNode.setNextNode(placeholderNode);
      placeholderNode.setPreviousNode(previousNode);
    }

    return children;
  }

  private toModelPath(path: string) {
    return path.replace(/-/g, '.');
  }

  /**
   * Enhance the provided action model with properties set on the parent test action groups.
   * Goes through the list of parent groups if any and sets the properties directly on the action model.
   */
  private async updateTestActionModel(path: string, actionName: string, actionModel: TestAction): Promise<void> {
    const result = await CitrusTestSchemaService.getTestActionDefinition(actionName);
    if (!isDefined(result?.definition.group)) {
      return;
    }
    const groups = result?.groups ?? [];
    const basePath = path.split('.').slice(0, -1).join('.');
    let groupPath = '';
    for (const group of groups) {
      groupPath = groupPath.length === 0 ? group.name : `${groupPath}.${group.name}`;
      this.applyGroupPropertiesToActionModel(group, `${basePath}.${groupPath}`, actionModel);
    }
  }

  private applyGroupPropertiesToActionModel(
    group: { propertiesSchema?: { properties?: Record<string, unknown> }; name: string },
    sourcePath: string,
    actionModel: TestAction,
  ) {
    if (!group.propertiesSchema?.properties) {
      return;
    }
    for (const key of Object.keys(group.propertiesSchema.properties)) {
      const value = getValue(this.test, `${sourcePath}.${key}`);
      if (isDefined(value)) {
        setValue(actionModel, key, value);
      }
    }
  }

  /**
   * Go through the list of test actions and rebuild proper test action group hierarchy.
   * Properties that belong to the test action group are removed from the test action model.
   */
  private async updateTestGroupModel(actions: TestActions[]): Promise<void> {
    if (!Array.isArray(actions)) {
      return;
    }

    for (const action of actions) {
      await this.updateActionGroupModel(action);
    }
  }

  private async updateActionGroupModel(action: TestActions): Promise<void> {
    const jsonRecord = action as Record<string, unknown>;
    for (const key in jsonRecord) {
      if (jsonRecord[key] === undefined) continue;
      const topResult = await CitrusTestSchemaService.getTestActionDefinition(key);
      if (!topResult) {
        await this.recurseIntoContainerActions(action, key);
        break;
      }
      const actionName =
        topResult.definition.kind === CatalogKind.TestActionGroup
          ? await CitrusTestVisualEntity.resolveGroupActionName(jsonRecord[key] as TestAction, key)
          : key;
      const actionResult = await CitrusTestSchemaService.getTestActionDefinition(actionName);
      if (isDefined(actionResult?.definition.group)) {
        this.moveGroupPropertiesFromAction(action, actionName, actionResult);
      }
      await this.recurseIntoContainerActions(action, actionName);
      break;
    }
  }

  private moveGroupPropertiesFromAction(
    action: TestActions,
    actionName: string,
    actionResult: Awaited<ReturnType<typeof CitrusTestSchemaService.getTestActionDefinition>>,
  ) {
    const groups = actionResult?.groups ?? [];
    let groupPath = '';
    for (const group of groups) {
      groupPath = groupPath.length === 0 ? group.name : `${groupPath}.${group.name}`;
      if (!group.propertiesSchema?.properties) {
        continue;
      }
      for (const key of Object.keys(group.propertiesSchema.properties)) {
        const value = getValue(action, `${this.toModelPath(actionName)}.${key}`);
        if (isDefined(value)) {
          unset(action, `${this.toModelPath(actionName)}.${key}`);
          setValue(action, `${groupPath}.${key}`, value);
        }
      }
    }
  }

  /**
   * Resolves the fully qualified action name for a TestActionGroup using DynamicCatalogRegistry.
   */
  private static async resolveGroupActionName(action: TestAction, groupName: string): Promise<string> {
    const jsonRecord = action as Record<string, unknown>;
    for (const key in jsonRecord) {
      if (jsonRecord[key] === undefined) continue;
      const candidateName = `${groupName}-${key}`;
      const candidateResult = await CitrusTestSchemaService.getTestActionDefinition(candidateName);
      if (!candidateResult) continue;
      if (candidateResult.definition.kind === CatalogKind.TestActionGroup) {
        return CitrusTestVisualEntity.resolveGroupActionName(jsonRecord[key] as TestAction, candidateName);
      }
      return candidateName;
    }
    return groupName;
  }

  private async recurseIntoContainerActions(action: TestActions, actionName: string): Promise<void> {
    const containerSettings = CitrusTestSchemaService.getTestContainerSettings(actionName);
    if (!containerSettings) {
      return;
    }

    const nestedValue = getValue(action, `${this.toModelPath(actionName)}.${containerSettings.name}`);
    if (!isDefined(nestedValue)) {
      return;
    }

    const nested: TestActions[] = containerSettings.type === 'single-node' ? [nestedValue] : nestedValue;
    if (Array.isArray(nested) && nested.length) {
      await this.updateTestGroupModel(nested);
    }
  }
}
